import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { OperationLabel } from '@core/models/operation-label.model';
import { LabelColorStyles, labelColorStyles } from '@shared/utils/label-color.util';

/**
 * A label pill in the label's colour (PB-5281): load cards, the Load Details
 * drawer and the label filters. Inactive labels get a dashed border.
 */
@Component({
  selector: 'hmt-label-chip',
  templateUrl: './label-chip.component.html',
  styleUrls: ['./label-chip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LabelChipComponent {
  @Input() set label(label: OperationLabel) {
    this.labelValue = label;
    this.styles = labelColorStyles(label?.color);
  }
  /** chip: drawer/card size (25px); pill: dropdown option size (21px). */
  @Input() size: 'chip' | 'pill' = 'chip';
  @Input() removable = false;
  @Input() showInactive = true;
  /** Show the full name in a tooltip when the chip is too narrow and the name is ellipsised. */
  @Input() overflowTooltip = false;
  @Output() removed = new EventEmitter<OperationLabel>();

  labelValue: OperationLabel;
  styles: LabelColorStyles = labelColorStyles(null);

  get inactive(): boolean {
    return this.showInactive && !!this.labelValue && !this.labelValue.active;
  }

  get tooltipText(): string {
    const name = this.labelValue?.name || '';
    return this.inactive ? `${name} · Inactive` : name;
  }

  /**
   * Runs before MatTooltip's own mouseenter listener (that one is attached in its
   * ngAfterViewInit), so the tooltip only opens when the name is actually truncated.
   */
  syncTooltip(nameEl: HTMLElement, tooltip: MatTooltip) {
    tooltip.disabled = !this.overflowTooltip || nameEl.scrollWidth <= nameEl.clientWidth;
  }

  remove(event: MouseEvent) {
    event.stopPropagation();
    this.removed.emit(this.labelValue);
  }
}
